import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  FileIcon,
  Video,
  Loader2,
  Link as LinkIcon,
  Calendar,
  ChevronDown,
  ExternalLink,
  Search,
} from "lucide-react";
import { showToast } from "@/components/ui/Toast";
import {
  createDeliverable,
  Deliverable,
  getDraftBlogPosts,
  DraftBlogPost,
} from "@/actions/delivery-hub";

interface AddDeliverableSliderProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId: number;
  schoolName: string;
  onSuccess?: (deliverable: Deliverable) => void;
}

type DeliverableType = "blog" | "video";

interface DeliverableFormData {
  title: string;
  description: string;
  type: DeliverableType;
  url: string;
  delivery_month: number;
  delivery_year: number;
}

const currentDate = new Date();
const initialFormData: DeliverableFormData = {
  title: "",
  description: "",
  type: "blog",
  url: "",
  delivery_month: currentDate.getMonth() + 1, // 1-12
  delivery_year: currentDate.getFullYear(),
};

const months = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// Generate years (current year and 2 years back/forward)
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const deliverableTypes: { value: DeliverableType; label: string; icon: typeof FileIcon; color: string }[] = [
  { value: "blog", label: "Blog Post", icon: FileIcon, color: "text-brand bg-brand/10 border-brand/30" },
  { value: "video", label: "Video", icon: Video, color: "text-pink-600 bg-pink-50 border-pink-300" },
];

const AddDeliverableSlider = ({
  isOpen,
  onClose,
  schoolId,
  schoolName,
  onSuccess,
}: AddDeliverableSliderProps) => {
  const [formData, setFormData] = useState<DeliverableFormData>(initialFormData);
  const [saving, setSaving] = useState(false);

  // Draft posts state
  const [draftPosts, setDraftPosts] = useState<DraftBlogPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [selectedPost, setSelectedPost] = useState<DraftBlogPost | null>(null);
  const [postDropdownOpen, setPostDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPosts, setTotalPosts] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Animation states
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle animation
  useEffect(() => {
    if (isOpen && !shouldRender) {
      setShouldRender(true);
      setTimeout(() => {
        setIsAnimating(true);
      }, 50);
    } else if (!isOpen && shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setFormData(initialFormData);
        setSelectedPost(null);
        setDraftPosts([]);
        setSearchQuery("");
        setPostDropdownOpen(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Fetch draft posts when opened and type is blog
  useEffect(() => {
    if (isOpen && schoolId && formData.type === "blog") {
      fetchDraftPosts();
    }
  }, [isOpen, schoolId, formData.type]);

  // Debounced search
  useEffect(() => {
    if (!postDropdownOpen || formData.type !== "blog") return;

    const timer = setTimeout(() => {
      fetchDraftPosts(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, postDropdownOpen]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setPostDropdownOpen(false);
      }
    };

    if (postDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [postDropdownOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (postDropdownOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [postDropdownOpen]);

  // Prevent body scroll when slider is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const fetchDraftPosts = useCallback(async (search?: string) => {
    setLoadingPosts(true);
    const result = await getDraftBlogPosts(schoolId, search);
    setDraftPosts(result.posts);
    setTotalPosts(result.totalPosts);
    setLoadingPosts(false);
  }, [schoolId]);

  const handlePostSelect = (post: DraftBlogPost) => {
    setSelectedPost(post);
    setFormData({
      ...formData,
      title: post.title,
      url: post.slug || "",
    });
    setPostDropdownOpen(false);
    setSearchQuery("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showToast("Title is required", "error");
      return;
    }

    if (formData.type !== "blog" && !formData.url.trim()) {
      showToast("URL is required", "error");
      return;
    }

    try {
      setSaving(true);
      const result = await createDeliverable(schoolId, {
        title: formData.title,
        description: formData.description || undefined,
        type: formData.type,
        url: formData.url || undefined,
        delivery_month: formData.delivery_month,
        delivery_year: formData.delivery_year,
      });

      if (result) {
        showToast("Deliverable added successfully", "success");
        onSuccess?.(result);
        onClose();
      } else {
        showToast("Failed to add deliverable", "error");
      }
    } catch (error) {
      showToast("Failed to add deliverable", "error");
    } finally {
      setSaving(false);
    }
  };

  const selectedType = deliverableTypes.find((t) => t.value === formData.type);

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 cursor-pointer ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl transition-transform duration-300 ease-out ${
          isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center">
                <FileIcon size={20} className="text-brand" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Add Deliverable</h2>
                <p className="text-sm text-gray-500">{schoolName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deliverable Type
                </label>
                <div className="flex gap-2">
                  {deliverableTypes.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.type === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, type: type.value, title: "", url: "" });
                          setSelectedPost(null);
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                          isSelected
                            ? type.color + " border-current"
                            : "border-gray-200 hover:border-gray-300 text-gray-600"
                        }`}
                      >
                        <Icon size={18} />
                        <span className="text-sm font-medium">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Month Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar size={14} className="inline mr-1" />
                  Delivery Month *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={formData.delivery_month}
                    onChange={(e) => setFormData({ ...formData, delivery_month: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  >
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={formData.delivery_year}
                    onChange={(e) => setFormData({ ...formData, delivery_year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Select the month this deliverable is for
                </p>
              </div>

              {/* Draft Blog Posts Selector (only for blog type) */}
              {formData.type === "blog" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Draft Blog Post
                  </label>
                  <div className="relative" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setPostDropdownOpen(!postDropdownOpen);
                        if (!postDropdownOpen) {
                          setSearchQuery("");
                        }
                      }}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-white border border-gray-300 rounded-lg text-left hover:border-gray-400 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        {selectedPost ? (
                          <span className="text-sm text-gray-900 truncate block">{selectedPost.title}</span>
                        ) : (
                          <span className="text-sm text-gray-500">Search draft posts...</span>
                        )}
                      </div>
                      <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${postDropdownOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Dropdown with Search */}
                    {postDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                        {/* Search Input */}
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              ref={searchInputRef}
                              type="text"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              placeholder="Type to search drafts..."
                              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-brand focus:border-brand"
                            />
                          </div>
                        </div>

                        {/* Results */}
                        <div className="max-h-60 overflow-y-auto">
                          {loadingPosts ? (
                            <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                              <Loader2 size={16} className="animate-spin" />
                              Searching...
                            </div>
                          ) : draftPosts.length === 0 ? (
                            <div className="py-4 text-center text-sm text-gray-500">
                              {searchQuery ? `No drafts found for "${searchQuery}"` : "No draft posts available"}
                            </div>
                          ) : (
                            <>
                              {draftPosts.map((post) => (
                                <button
                                  key={post.id}
                                  type="button"
                                  onClick={() => handlePostSelect(post)}
                                  className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                                    selectedPost?.id === post.id ? "bg-brand/5" : ""
                                  }`}
                                >
                                  <p className="text-sm text-gray-900 font-medium truncate">{post.title}</p>
                                  <p className="text-xs text-gray-500 truncate mt-0.5">{post.slug}</p>
                                </button>
                              ))}
                              {totalPosts > draftPosts.length && (
                                <div className="px-3 py-2 text-xs text-gray-400 bg-gray-50 text-center">
                                  Showing {draftPosts.length} of {totalPosts} posts. Type to search more.
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Search and select a draft, or enter details manually below
                  </p>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (selectedPost) setSelectedPost(null);
                  }}
                  placeholder={`Enter ${selectedType?.label.toLowerCase()} title`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                  required
                />
              </div>

              {/* URL - only shown for non-blog types (blog uses review links) */}
              {formData.type !== "blog" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL *
                  </label>
                  <div className="relative">
                    <LinkIcon
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => {
                        setFormData({ ...formData, url: e.target.value });
                        if (selectedPost) setSelectedPost(null);
                      }}
                      placeholder="https://..."
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand"
                      required
                    />
                    {formData.url && (
                      <a
                        href={formData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand"
                        title="Open in new tab"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Paste the link to the {selectedType?.label.toLowerCase()}
                  </p>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add a brief description (optional)"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand resize-none"
                />
              </div>

            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !formData.title.trim() || (formData.type !== "blog" && !formData.url.trim())}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover disabled:opacity-50"
              >
                {saving && <Loader2 size={16} className="mr-2 animate-spin" />}
                Add Deliverable
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDeliverableSlider;
